// libs
import React, { memo, useState, useEffect, useRef } from 'react';

// helpers
import { inPriceFormat } from 'shared/helpers/utils';

// styles
import {
  Container,
  Discount,
  Amount,
  OriginalPrice,
  Currency,
  Text as GradientText,
  AnimatedPriceContainer,
  AnimatedText,
} from './styles';

// constants
import { DEFAULT_CURRENCY } from 'shared/constants';

// hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

const DISCOUNT_TEXT = '%';
const ARIA_LABELS = {
  DISCOUNTED_PRICE: 'Discounted price',
  ORIGINAL_PRICE: 'Original price',
  DISCOUNT_PERCENTAGE: 'Discount percentage',
};

export interface IPriceWithDiscountProps {
  discount?: string | number;
  discountedPrice?: number;
  price: string | number;
  hidePercentage?: boolean;
  className?: string;
  salePricePrticipatingEnabled?: boolean;
  priceCartRuleEnabled?: boolean;
  recentlyViewed?: boolean;
  cartRuleEnabled?: boolean;
}

function Price({
  discount = 0,
  discountedPrice,
  price,
  hidePercentage = false,
  className,
  salePricePrticipatingEnabled = false,
  priceCartRuleEnabled = false,
  recentlyViewed = false,
  cartRuleEnabled = false
}: IPriceWithDiscountProps) {
  const { enabled: saleExpEnabled, theme, saleCommunication } = useRemoteConfig('saleExperience') || {};
  const { plp } = saleCommunication || {};
  const saleExp = saleExpEnabled;
  const {
    animation: { enabled: animationEnabled,
      timerMs, animationText, sessionCount } = {}
  } = plp || {};

  const [saleTextVisible, setSaleTextVisible] = useState(true);
  const [saleTextExiting, setSaleTextExiting] = useState(false);
  const [priceVisible, setPriceVisible] = useState(false);
  const [priceExiting, setPriceExiting] = useState(false);
  const sessionCounter = useRef(0);

  useEffect(() => {
    if (!saleExp) return;

    const interval = setInterval(() => {
      // Stop showing animation after configured number of times
      if (sessionCounter.current >= sessionCount) {
        clearInterval(interval);
        return;
      }

      sessionCounter.current += 1;
      if (saleTextVisible) {
        // Sale text is visible, start its exit and price entry SIMULTANEOUSLY
        setSaleTextExiting(true);
        setPriceVisible(true); // Start immediately, no delay

        setTimeout(() => {
          // Complete sale text exit
          setSaleTextVisible(false);
          setSaleTextExiting(false);
        }, 300);
      } else {
        // Price is visible, start its exit and sale text entry SIMULTANEOUSLY
        setPriceExiting(true);
        setSaleTextVisible(true); // Start immediately, no delay

        setTimeout(() => {
          // Complete price exit
          setPriceVisible(false);
          setPriceExiting(false);
        }, 300);
      }
    }, timerMs);

    return () => clearInterval(interval);
  }, [saleExp, saleTextVisible, sessionCount, timerMs]);

  if (price === undefined) {
    return null;
  }

  const formattedPrice = inPriceFormat(price);
  const numericDiscount = typeof discount === 'string' ? parseFloat(discount) : discount;

  const renderAnimatedDiscount = () => (
    <Container
      className={className}
      data-test="discount-price-container"
      data-testid="discount-price-container"
      saleExp={saleExp}
      bg={theme.bgGradient}
    >
      <AnimatedPriceContainer>
        <AnimatedText
          isVisible={saleTextVisible && !saleTextExiting}
          className={saleTextExiting ? 'exit' : ''}
        >
          <GradientText gradientColors={theme.textGradient}>
            {animationText}
          </GradientText>
        </AnimatedText>

        <AnimatedText
          isVisible={priceVisible && !priceExiting}
          className={priceExiting ? 'exit' : ''}
        >
          <Amount aria-label={ARIA_LABELS.DISCOUNTED_PRICE} data-test="discounted-price">
            <Currency data-test="currency" />
            <GradientText gradientColors={theme.textGradient}>
              {DEFAULT_CURRENCY}
              {inPriceFormat(discountedPrice)}
            </GradientText>
          </Amount>
          <OriginalPrice aria-label={ARIA_LABELS.ORIGINAL_PRICE} data-test="original-price">
            <Currency data-test="currency">{DEFAULT_CURRENCY}</Currency>
            {formattedPrice}
          </OriginalPrice>
          {!hidePercentage && (
            <Discount aria-label={ARIA_LABELS.DISCOUNT_PERCENTAGE} data-test="discount-percent">
              {numericDiscount}
              {DISCOUNT_TEXT}
            </Discount>
          )}
        </AnimatedText>
      </AnimatedPriceContainer>
    </Container>
  );

  const renderStaticDiscount = () => (
    <Container className={className} data-test="discount-price-container" data-testid="discount-price-container">
      <Amount aria-label={ARIA_LABELS.DISCOUNTED_PRICE} data-test="discounted-price">
        <Currency data-test="currency">{DEFAULT_CURRENCY}</Currency>
        {inPriceFormat(discountedPrice)}
      </Amount>
      <OriginalPrice aria-label={ARIA_LABELS.ORIGINAL_PRICE} data-test="original-price">
        <Currency data-test="currency">{DEFAULT_CURRENCY}</Currency>
        {formattedPrice}
      </OriginalPrice>
      {!hidePercentage && (
        <Discount data-test="discount-percent">
          {numericDiscount}
          {DISCOUNT_TEXT}
        </Discount>
      )}
    </Container>
  );

  return numericDiscount > 0 ? (
    animationEnabled && salePricePrticipatingEnabled && !recentlyViewed ?
      (renderAnimatedDiscount()) : (
        cartRuleEnabled ? renderStaticDiscount() : (
          animationEnabled && priceCartRuleEnabled && !recentlyViewed ? renderAnimatedDiscount() : renderStaticDiscount()
        )
      )
  ) : (
    <Container className={className} data-test="no-discount-price-container" data-testid="no-discount-price-container">
      <Amount data-test="amount">
        <Currency data-test="currency">{DEFAULT_CURRENCY}</Currency>
        {formattedPrice}
      </Amount>
    </Container>
  );
}

export default memo(Price);
